import { FirstStepFormData } from "../schemas/validationSchemas";
import { PoolInfo, SeasonMonth } from "../context/types";
import { calculateSurfaceArea } from "../utils/calculateSurface";
import { calculateHeat } from "../utils/calculateHeat";
import { calcualteMinMachinePower } from "../utils/calculateMinAndMaxMachinePower";
import {
  calculateMaxMachinePowerByMonth,
  findHighestPowerAndIndex,
  calculateHeatCoefficient,
  calculateDegreesPower,
  calculatePumpCapacity,
  calculatePricesData,
} from "../utils/poolCalculationHelpers";
import {
  getAnnualCOP,
  getFirstHeating,
} from "../components/ResultStep/utils/calculateValues";
import { pumpModels } from "../constants/pumpData";

export interface PumpRecommendation {
  capacity: number;
  power: number;
  totalCost: number;
  totalPrice: number;
  monthlyCosts: number[];
  consumption: number[];
  annualCOP: number;
  firstHeating:
    | {
        minMonth: number;
        maxMonth: number;
        min: number;
        max: number;
      }
    | undefined;
  highestSpeedRate: number;
  pumpModel: any;
}

export interface PumpRecommendations {
  standard: PumpRecommendation;
  premium: PumpRecommendation;
  bestValue: "standard" | "premium";
  poolMeasures: {
    surfaceArea: number;
    volume: number;
  };
  powerRequirements: {
    small: number;
    big: number;
  };
  isMultiplePumps: boolean;
  isTheSamePump: boolean;
  pumpCounter: number;
  temperaturePerMonth: Record<string, number>;
  usesLowTemperatureCOP: boolean;
  airTemperatureLabel: string;
  monthsArray: { name: string; value: boolean }[];
  heatDemandLoss: number[];
}

function transformFormDataToPoolInfo(formData: FirstStepFormData): PoolInfo {
  return {
    shape: formData.shape!,
    depth: formData.depth,
    type: null,
    country: formData.country,
    state: formData.state,
    city: formData.city,
    postcode: formData.postcode,
    location: formData.location!,
    lowTemp: 0,
    electricityCost: formData.electricityCost,
    withCover: formData.withCover === "Yes",
    operatingHours: formData.operatingHours,
    requirePoolTemp: formData.requirePoolTemp,
    poolType: null,
    poolMaterial: formData.poolMaterial!,
    diameter: formData.diameter || 0,
    length: formData.length || 0,
    width: formData.width || 0,
    surfaceArea: formData.surfaceArea || 0,
  };
}

function createMonthsAndTemperatureData(
  operatingMonths: string[],
  temperatureData: Record<string, number> = {}
): {
  months: Record<string, SeasonMonth>;
  temperatureArray: number[];
  temperatureRecord: Record<string, number>;
  operatingTemperatureArray: number[];
  usesLowTemperatureCOP: boolean;
  airTemperatureLabel: string;
} {
  const allMonths = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const monthsMap: Record<string, SeasonMonth> = {};
  const temperatureArray: number[] = [0];
  const temperatureRecord: Record<string, number> = {};
  const operatingTemperatureArray: number[] = [0];

  allMonths.forEach((month, index) => {
    const isOperating = operatingMonths.some(
      (opMonth) =>
        opMonth.toLowerCase() === month.toLowerCase() ||
        opMonth === month ||
        opMonth.toLowerCase() === month.toLowerCase().substring(0, 3) ||
        month.toLowerCase().startsWith(opMonth.toLowerCase())
    );

    monthsMap[month] = {
      index,
      name: month,
      value: isOperating,
    };

    const monthIndex = (index + 1).toString();
    const temp = temperatureData[monthIndex] || 0;

    temperatureArray.push(temp);
    temperatureRecord[(index + 1).toString()] = temp;

    operatingTemperatureArray.push(isOperating ? temp : 0);
  });

  return {
    months: monthsMap,
    temperatureArray,
    temperatureRecord,
    operatingTemperatureArray,
    usesLowTemperatureCOP: false,
    airTemperatureLabel: "27°C",
  };
}

export function calculatePumpRecommendations(
  formData: FirstStepFormData
): PumpRecommendations {
  const poolInfo = transformFormDataToPoolInfo(formData);

  const monthsAndTemperature = createMonthsAndTemperatureData(
    formData.operatingMonths,
    formData.temperatureData
  );

  const { surfaceAreaTop, volume } = calculateSurfaceArea({ info: poolInfo });

  const heatData = calculateHeat({
    info: poolInfo,
    monthTemperatureByCity: monthsAndTemperature.operatingTemperatureArray,
    surfaceAreaTop,
  });

  const maxMachinePowerReal = calcualteMinMachinePower({
    heatDemandOrLoss: heatData.heatDemandLoss,
    poolInfo,
  }).maxMachinePowerReal;

  const maxMachinePowerByAvailableMonth = calculateMaxMachinePowerByMonth(
    monthsAndTemperature.months,
    maxMachinePowerReal
  );

  const { higher, higherIndex } = findHighestPowerAndIndex(
    maxMachinePowerByAvailableMonth
  );

  const finalHeatCoefficient = calculateHeatCoefficient(
    monthsAndTemperature.temperatureRecord,
    higherIndex
  );

  const degreesPower = calculateDegreesPower(higher, finalHeatCoefficient);

  const pumpCapacity = calculatePumpCapacity(
    degreesPower.small,
    monthsAndTemperature.usesLowTemperatureCOP
  );

  const pricesData = calculatePricesData(
    heatData,
    monthsAndTemperature.months,
    pumpCapacity,
    poolInfo,
    monthsAndTemperature.usesLowTemperatureCOP
  );

  const monthsArray = Object.values(monthsAndTemperature.months);

  const { standardAnnualCop, premiumAnnualCop } = getAnnualCOP({
    heatDemandLoss: heatData.heatDemandLoss,
    months: monthsArray,
    bigModelEnergyConsumption: pricesData.bigConsumption,
    smallModelEnergyConsumption: pricesData.smallConsumption,
    counter: pumpCapacity.counter,
  });

  const { firstHeatingStandard, firstHeatingPremium } = getFirstHeating({
    heatData,
    poolInfo,
    months: monthsArray,
    temperaturePerMonth: monthsAndTemperature.temperatureRecord,
    standartPumpCapacity: pumpCapacity.standart,
    premiumPumpCapacity: pumpCapacity.premium,
    measures: { surfaceAreaTop, estimateVol: volume },
    counter: pumpCapacity.counter,
  });

  const standartHighestSpeedRate = Number(
    (
      ((degreesPower.small / pumpCapacity.standart) * 100) /
      pumpCapacity.counter
    ).toFixed(0)
  );
  const premiumHighestSpeedRate = Number(
    ((degreesPower.small / pumpCapacity.premium) * 100).toFixed(0)
  );

  const standartPump = pumpModels.find((pump) => {
    const capacityToCheck = pump.onAir26?.capacity || pump.onAir15.capacity;
    return capacityToCheck === pumpCapacity.standart;
  });

  const premiumPump = pumpModels.find((pump) => {
    const capacityToCheck = pump.onAir26?.capacity || pump.onAir15.capacity;
    return capacityToCheck === pumpCapacity.premium;
  });

  const isTheSamePump = pumpCapacity.premium === pumpCapacity.standart;
  const isMultiplePumps = pumpCapacity.counter === 2;

  return {
    standard: {
      capacity: pumpCapacity.standart,
      power: degreesPower.small,
      totalCost: pricesData.totalStandartPrice,
      totalPrice: pricesData.totalStandartPrice,
      monthlyCosts: pricesData.standartPricePerMonth,
      consumption: pricesData.smallConsumption,
      annualCOP: standardAnnualCop,
      firstHeating: firstHeatingStandard || undefined,
      highestSpeedRate: standartHighestSpeedRate,
      pumpModel: standartPump,
    },
    premium: {
      capacity: pumpCapacity.premium,
      power: degreesPower.big,
      totalCost: pricesData.totalPremiumPrice,
      totalPrice: pricesData.totalPremiumPrice,
      monthlyCosts: pricesData.premiumPricePerMonth,
      consumption: pricesData.bigConsumption,
      annualCOP: premiumAnnualCop,
      firstHeating: firstHeatingPremium || undefined,
      highestSpeedRate: premiumHighestSpeedRate,
      pumpModel: premiumPump,
    },
    bestValue: (pumpCapacity.standart > 0 ? "standard" : "premium") as
      | "standard"
      | "premium",
    poolMeasures: {
      surfaceArea: surfaceAreaTop,
      volume: Number(volume.toFixed(2)),
    },
    powerRequirements: {
      small: degreesPower.small,
      big: degreesPower.big,
    },
    isMultiplePumps,
    isTheSamePump,
    pumpCounter: pumpCapacity.counter,
    temperaturePerMonth: monthsAndTemperature.temperatureRecord,
    usesLowTemperatureCOP: monthsAndTemperature.usesLowTemperatureCOP,
    airTemperatureLabel: monthsAndTemperature.airTemperatureLabel,
    monthsArray: monthsArray,
    heatDemandLoss: heatData.heatDemandLoss,
  };
}
